import useParamCleaner from '@/composables/view-params/clean';
import { DateTime } from 'luxon';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { publicViewApplications } from '@/lib/saved-view/saved-view';
import { viewType } from 'shared/constants.json';

export const normalizeDateToISO = (d) => {
  if (isNullOrUndefined(d)) return d;
  const dt = DateTime.fromISO(d);
  if (!dt.isValid) return d;
  return dt.toISO();
};

export const normalizeTimeRangeDates = (tree) => {
  if (isNullOrUndefined(tree)) return tree;
  if (Array.isArray(tree.children) && tree.children.length > 0) {
    return { ...tree, children: tree.children.map(normalizeTimeRangeDates) };
  }
  if (isNullOrUndefined(tree.scope) || isNullOrUndefined(tree.scope.timeRange)) return tree;
  return {
    ...tree,
    scope: {
      ...tree.scope,
      timeRange: {
        ...tree.scope.timeRange,
        start: normalizeDateToISO(tree.scope.timeRange.start),
        end: normalizeDateToISO(tree.scope.timeRange.end),
      },
    },
  };
};

export default function useSavedViewCleaner() {
  const paramCleaner = useParamCleaner();

  const clearProps = ({ view, props = [] }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params) || isNullOrUndefined(view.params.props)) {
      return view;
    }

    return {
      ...view,
      params: paramCleaner.clearProps(view.params, props),
    };
  };

  const setDefaultWidth = ({ view, props }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params) || isNullOrUndefined(view.params.props)) {
      return view;
    }

    if (![viewType.cascade].includes(view.viewType)) {
      return view;
    }

    return {
      ...view,
      params: paramCleaner.setDefaultWidth(view.params, props),
    };
  };

  const clearGrouping = ({ view }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params)) {
      return view;
    }

    if (![viewType.atlas].includes(view.viewType)) {
      return view;
    }

    return {
      ...view,
      params: paramCleaner.clearGrouping(view.params),
    };
  };

  const clearIsPublic = ({ view }) => {
    if (isNullOrUndefined(view)) {
      return view;
    }

    const isPublic = typeof view.isPublic !== 'undefined' ? view.isPublic : publicViewApplications.includes(view.viewApplication);
    return {
      ...view,
      isPublic,
    };
  };

  const clearOrder = ({ view, props }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params) || isNullOrUndefined(view.params.order)) {
      return view;
    }

    return {
      ...view,
      params: paramCleaner.clearOrder(view.params, props),
    };
  };

  const clearFilter = ({ view, props = [] }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params) || isNullOrUndefined(view.params.filter)) {
      return view;
    }

    return {
      ...view,
      params: paramCleaner.clearFilter(view.params, props),
    };
  };

  const clearDateFormat = ({ view }) => {
    if (isNullOrUndefined(view) || isNullOrUndefined(view.params) || isNullOrUndefined(view.params.filter)) {
      return view;
    }

    return {
      ...view,
      params: {
        ...view.params,
        filter: normalizeTimeRangeDates(view.params.filter),
      },
    };
  };

  const clearView = ({ view, props = [] }) => {
    let clearedView = clearProps({ view, props });
    clearedView = setDefaultWidth({ view: clearedView, props });
    clearedView = clearFilter({ view: clearedView, props });
    clearedView = clearOrder({ view: clearedView, props });
    clearedView = clearGrouping({ view: clearedView });
    clearedView = clearIsPublic({ view: clearedView });
    clearedView = clearDateFormat({ view: clearedView });
    return clearedView;
  };

  return { clearView };
}
